import type { Doc } from '../../convex/_generated/dataModel';
import type { PromptType } from '../components/AIPromptGeneratorModal';

type ReferenceDoc = Doc<'references'> & {
  imageUrl?: string | null;
  sourceURL?: string | null;
  tags: Doc<'tags'>[];
  mediaItems: (Doc<'mediaItems'> & {
    url: string | null;
    mediaType: 'image' | 'video';
  })[];
  app?: Doc<'apps'> | null;
};

interface ProcessedMediaItem {
  url: string;
  type: 'image' | 'video';
  thumbnail?: string; // For videos, this will be the extracted frame
}

interface CollectedReferenceData {
  title: string;
  description?: string;
  sourceUrl?: string;
  appName?: string;
  appCategory?: string;
  tags: string[];
  category?: string;
  type?: string;
  mediaItems: ProcessedMediaItem[];
  totalMediaCount: number;
}

/**
 * Extracts a frame from a video URL to use as a thumbnail
 * This function creates a temporary video element and captures a frame
 */
async function extractVideoFrame(videoUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    video.crossOrigin = 'anonymous';
    video.muted = true;
    
    video.onloadedmetadata = () => {
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Seek to 1 second or 10% of duration, whichever is smaller
      const seekTime = Math.min(1, video.duration * 0.1);
      video.currentTime = seekTime;
    };

    video.onseeked = () => {
      try {
        // Draw the current frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to base64 image
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        
        // Clean up
        video.remove();
        canvas.remove();
        
        resolve(dataUrl);
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    };

    video.onerror = () => {
      reject(new Error('Failed to load video'));
    };

    video.src = videoUrl;
    video.load();
  });
}

/**
 * Processes all media items from a reference, extracting thumbnails from videos
 */
async function processMediaItems(mediaItems: ReferenceDoc['mediaItems'], legacyImageUrl?: string | null): Promise<ProcessedMediaItem[]> {
  const processed: ProcessedMediaItem[] = [];

  // Process mediaItems array
  for (const item of mediaItems) {
    if (!item.url) continue;

    if (item.mediaType === 'video') {
      try {
        // Extract a frame from the video to use as thumbnail
        const thumbnail = await extractVideoFrame(item.url);
        processed.push({
          url: item.url,
          type: 'video',
          thumbnail
        });
      } catch (error) {
        console.warn('Failed to extract video frame:', error);
        // Still include the video without thumbnail
        processed.push({
          url: item.url,
          type: 'video'
        });
      }
    } else {
      processed.push({
        url: item.url,
        type: 'image'
      });
    }
  }

  // Handle legacy imageUrl if no other media items exist
  if (processed.length === 0 && legacyImageUrl) {
    processed.push({
      url: legacyImageUrl,
      type: 'image'
    });
  }

  return processed;
}

/**
 * Collects and formats all relevant data from a reference for AI processing
 */
export async function collectReferenceData(reference: ReferenceDoc): Promise<CollectedReferenceData> {
  // Process media items
  const mediaItems = await processMediaItems(reference.mediaItems, reference.imageUrl);

  // Extract tag names
  const tags = reference.tags.map(tag => tag.name);

  // Collect basic metadata
  const data: CollectedReferenceData = {
    title: reference.title,
    description: reference.description || undefined,
    sourceUrl: reference.sourceURL || undefined,
    appName: reference.app?.name || undefined,
    appCategory: reference.app?.category || undefined,
    tags,
    category: reference.category || undefined,
    type: reference.type || undefined,
    mediaItems,
    totalMediaCount: mediaItems.length
  };

  return data;
}

/**
 * Generates a structured prompt for OpenAI based on the prompt type and reference data
 */
function generatePromptInstructions(
  promptType: PromptType,
  customInstructions?: string
): string {
  const baseInstructions = `You are an expert UI/UX developer and designer. Analyze the provided image(s) and metadata to create a detailed, actionable prompt that can be used by developers in AI coding assistants like Cursor, Claude, or ChatGPT.

Your response should be a complete prompt that a developer can copy and paste into their AI assistant to recreate or build something similar to what's shown in the reference.`;

  const promptTypeInstructions: Record<PromptType, string> = {
    'react-component': `
Focus on creating a prompt for building a reusable React component. The prompt should include:

1. **Component Structure**: Describe the component's purpose, props interface, and main functionality
2. **Visual Design**: Detailed description of the layout, styling, colors, typography, and spacing
3. **Interactive Elements**: Any buttons, forms, hover states, animations, or user interactions
4. **Implementation Details**: Specific React patterns, hooks, state management, and best practices
5. **Styling Approach**: Whether to use CSS modules, Tailwind, styled-components, etc.
6. **Accessibility**: ARIA labels, keyboard navigation, and screen reader considerations
7. **Responsive Design**: How the component should adapt to different screen sizes

Format your response as a clear, detailed prompt that starts with "Create a React component that..." and includes all necessary technical specifications.`,

    'full-page': `
Focus on creating a prompt for building a complete page layout. The prompt should include:

1. **Page Structure**: Overall layout, sections, header, footer, navigation, and content areas
2. **Content Organization**: How information is structured and prioritized
3. **Visual Hierarchy**: Typography scales, color schemes, spacing systems, and visual flow
4. **Interactive Features**: Navigation, forms, buttons, modals, and any dynamic functionality
5. **Responsive Design**: How the layout adapts across desktop, tablet, and mobile devices
6. **Performance Considerations**: Image optimization, loading strategies, and core web vitals
7. **SEO Elements**: Meta tags, semantic HTML, and structured data
8. **Technology Stack**: Recommended frameworks, libraries, and tools

Format your response as a comprehensive prompt that starts with "Create a full page layout that..." and provides detailed specifications for each section.`,

    'feature-implementation': `
Focus on creating a prompt for implementing a specific feature or functionality. The prompt should include:

1. **Feature Definition**: Clear description of what the feature does and its purpose
2. **User Flow**: Step-by-step interaction flow and user journey
3. **Technical Requirements**: APIs, data structures, state management, and backend considerations
4. **UI Components**: Specific interface elements, forms, buttons, and display components needed
5. **Business Logic**: Rules, validations, calculations, and processing requirements
6. **Error Handling**: Edge cases, validation messages, and error recovery
7. **Testing Strategy**: Unit tests, integration tests, and user acceptance criteria
8. **Integration Points**: How this feature connects with existing systems or components

Format your response as a detailed prompt that starts with "Implement a feature that..." and includes comprehensive technical and functional specifications.`,

    'design-system': `
Focus on creating a prompt for building a design system component. The prompt should include:

1. **Component Definition**: Purpose, variants, and use cases within the design system
2. **Design Tokens**: Colors, typography, spacing, shadows, and other design variables
3. **Component Variants**: Different sizes, states, and configurations
4. **Composition Patterns**: How this component works with others in the system
5. **Documentation**: Props API, usage examples, and guidelines
6. **Consistency Rules**: How to maintain design consistency across implementations
7. **Accessibility Standards**: WCAG compliance and inclusive design practices
8. **Theming Support**: Dark mode, custom themes, and brand variations

Format your response as a systematic prompt that starts with "Create a design system component that..." and includes detailed specifications for consistency and reusability.`,

    'ui-pattern': `
Focus on creating a prompt for implementing a UI pattern or interaction. The prompt should include:

1. **Pattern Definition**: What interaction or interface pattern is being implemented
2. **User Experience**: How users interact with the pattern and expected behaviors
3. **Visual States**: Default, hover, active, focus, disabled, and loading states
4. **Animation Details**: Transitions, micro-interactions, and motion design
5. **Accessibility Patterns**: Keyboard navigation, screen reader support, and ARIA patterns
6. **Responsive Behavior**: How the pattern adapts to different screen sizes and input methods
7. **Implementation Approach**: Recommended libraries, frameworks, or vanilla approaches
8. **Best Practices**: Common pitfalls to avoid and optimization strategies

Format your response as a focused prompt that starts with "Implement a UI pattern that..." and provides detailed interaction and implementation guidance.`,

    'tailwind-theme': `
Focus on creating a prompt for generating a Tailwind CSS theme configuration based on the colors and design elements visible in the image(s). The prompt should include:

1. **Color Palette Extraction**: Identify and extract the primary, secondary, accent, and neutral colors from the image
2. **Color Naming Convention**: Provide semantic names for colors (primary, secondary, accent, success, warning, error, etc.)
3. **Color Variations**: Generate light, default, and dark variations for each color family
4. **Typography Scale**: Suggest font families, sizes, and weights that complement the visual style
5. **Spacing System**: Recommend spacing values that match the visual rhythm and proportions
6. **Border Radius**: Extract border radius values for buttons, cards, and other elements
7. **Shadow System**: Define shadow styles that match the depth and elevation in the design
8. **Component Tokens**: Suggest specific color applications for buttons, forms, cards, and other UI elements
9. **Accessibility Considerations**: Ensure color contrast ratios meet WCAG guidelines
10. **Theme Structure**: Provide a complete Tailwind config with proper color organization

Format your response as a detailed prompt that starts with "Create a Tailwind CSS theme configuration that..." and includes specific hex values, color names, and complete configuration code.`,

    'custom': `
Follow the custom instructions provided by the user while maintaining the same level of detail and structure as the other prompt types. Ensure the prompt is actionable and includes:

1. **Clear Objective**: What the developer should build or implement
2. **Technical Specifications**: Detailed requirements and constraints
3. **Visual Design**: Comprehensive description of the appearance and layout
4. **Functionality**: All interactive elements and behaviors
5. **Implementation Guidance**: Recommended approaches and best practices
6. **Quality Standards**: Accessibility, performance, and testing considerations

Custom Instructions: ${customInstructions || 'No custom instructions provided'}

Format your response as a complete prompt that a developer can immediately use in their AI assistant.`
  };

  return `${baseInstructions}

${promptTypeInstructions[promptType]}

Based on the image(s) and metadata provided, create a comprehensive prompt that captures all the visual and functional details needed to recreate or build something similar.`;
}

/**
 * Prepares the payload for OpenAI's Vision API
 */
function prepareOpenAIPayload(
  referenceData: CollectedReferenceData,
  promptType: PromptType,
  customInstructions?: string
) {
  const systemPrompt = generatePromptInstructions(promptType, customInstructions);
  
  // Prepare image content for the vision API
  const imageContent = referenceData.mediaItems.map(item => {
    // For videos, use the thumbnail; for images, use the original URL
    const imageUrl = item.type === 'video' && item.thumbnail ? item.thumbnail : item.url;
    
    return {
      type: 'image_url' as const,
      image_url: {
        url: imageUrl,
        detail: 'high' as const
      }
    };
  });

  // Prepare metadata text
  const metadataText = `
Reference Metadata:
- Title: ${referenceData.title}
- Description: ${referenceData.description || 'No description provided'}
- Source: ${referenceData.sourceUrl || 'No source URL'}
- App: ${referenceData.appName || 'Unknown'} ${referenceData.appCategory ? `(${referenceData.appCategory})` : ''}
- Category: ${referenceData.category || 'Not specified'}
- Type: ${referenceData.type || 'Not specified'}
- Tags: ${referenceData.tags.length > 0 ? referenceData.tags.join(', ') : 'No tags'}
- Media Count: ${referenceData.totalMediaCount} item(s)
`.trim();

  const messages = [
    {
      role: 'system' as const,
      content: systemPrompt
    },
    {
      role: 'user' as const,
      content: [
        {
          type: 'text' as const,
          text: metadataText
        },
        ...imageContent
      ]
    }
  ];

  return {
    model: 'gpt-4-vision-preview', // This will be updated to use the latest model
    messages,
    max_tokens: 2000,
    temperature: 0.7
  };
} 